import React from "react"
import { cva, type VariantProps } from "./cva.ts"
import { toWrappedChildren } from "./to-wrapped-children.ts"

export const Badge = ({ children, divided, kind, className }: Badge.Props) => {
  if (divided) {
    const items = Array.isArray(children) ? children : [children]
    return (
      <Container kind={kind} className={className}>
        {items.map((item, index) => {
          const isLastPart = index === items.length - 1
          return (
            <React.Fragment key={index}>
              {item}
              {!isLastPart && <Divider />}
            </React.Fragment>
          )
        })}
      </Container>
    )
  }

  return (
    <Container kind={kind} className={className}>
      {toWrappedChildren(children)}
    </Container>
  )
}

export namespace Badge {
  export type Part = string | React.ReactNode

  export type Props = {
    kind?: VariantProps<typeof classForBadge>["kind"]
    children: React.ReactNode | React.ReactNode[]
    divided?: boolean
    className?: string
  }
}

const Container = ({ kind, children, className }: Container.Props) => (
  <span data-ui="badge-container" className={classForBadge({ kind, className })}>
    {children}
  </span>
)

namespace Container {
  export type Props = VariantProps<typeof classForBadge> & {
    children: React.ReactNode
    className?: string
  }
}

const Divider = () => <span data-ui="badge-divider" className="w-px h-full my-0.5 bg-gray-6" />

export const classForBadge = cva(
  [
    "h-5",
    "inline-flex flex-grow-0 gap-1 items-center justify-center",
    "text-xs whitespace-nowrap",
    "rounded",
    "select-none",
    "[&>svg]:size-3 [&:has(>svg:only-child)>svg]:size-4",
    "[&:has(>svg:only-child)]:p-0.5",
    "[&:not(:has(>svg:only-child))]:px-1 [&:not(:has(>svg:only-child))]:py-1",
  ],
  {
    variants: {
      kind: {
        shaded: "text-gray-11 bg-gray-4",
        outline: "text-gray-12 bshadow bshadow-gray-6",
        information: "text-blue-12 bg-blue-3",
        positive: "text-grass-12 bg-grass-3",
        caution: "text-orange-12 bg-orange-3",
        danger: "text-tomato-11 bg-tomato-3",
      },
    },
    defaultVariants: {
      kind: "shaded",
    },
  },
)
